/* ManageRolesComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageRolesRenderer");

class ManageRolesComponent extends EventEmitter {
    constructor(
        userService,
        userSession,
        logger,
        ActionGroupEntityDef,
        userFeedback,
        CustomFieldsEntity,
        DeviceEntity,
        SiteEntity,
        uiUtils
    ) {
        super();
        this._actionGroupEntityDef = ActionGroupEntityDef;
        this._CustomFieldsEntityClass = CustomFieldsEntity;
        this._customFieldsEntityInstance = undefined;
        this._customFieldsWithInherited = {};
        this._DeviceEntityClass = DeviceEntity;
        this._SiteEntityClass = SiteEntity;
        this._userService = userService;
        this._userSession = userSession;
        this._myUserInfo = undefined;
        this._descendantOrgs = null;
        this._logger = logger;
        this._sitePaths = [];
        this._vendorMap = undefined;
        this._typeBaseMap = undefined;
        this._modalUserFeedback = userFeedback;
        this._uiUtils = uiUtils;
        this._getUserInfoProm = this.fetchUserInfo();
        this.getActionGroupsProm = this._getActionGroupsFeed();
        this._slickFormatData = undefined;
        this._activeOrgId = _.get(userSession, "activeOrganization.id");
        this._activeOrgName = _.get(userSession, "activeOrganization.name");
        this.actionGroupsFeed = undefined;
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}

    _registerFeedListeners() {
        this.actionGroupsFeed.on("EntityAdded", (this.feedUpdated = this.feedUpdated.bind(this)));
        this.actionGroupsFeed.on(
            "EntityPropertiesUpdated",
            (this.feedUpdated = this.feedUpdated.bind(this))
        );
        this.actionGroupsFeed.on("EntityRemoved", (this.feedUpdated = this.feedUpdated.bind(this)));
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get userInfo() {
        return this.getUserInfo();
    }
    getUserInfo() {
        return this._myUserInfo;
    }

    get slickData() {
        return this.getSlickData();
    }

    getSlickData() {
        return this._slickFormatData;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return this._activeOrgId;
    }

    shutdown() {
        this._unregisterListeners();
        if (this._customFieldsEntityInstance && this._customFieldsEntityInstance.shutdown) {
            this._customFieldsEntityInstance
                .shutdown()
                .catch((error) => this._logger.warn({error}));
        }
        return this._renderer
            ._shutdown()
            .then(() => this.actionGroupsFeed.shutdown())
            .then(
                () => {
                    this._renderer = undefined;
                    this._actionGroupEntityDef = undefined;
                    this._customFieldsEntityInstance = undefined;
                    this._CustomFieldsEntityClass = undefined;
                    this._DeviceEntityClass = undefined;
                    this._userService = undefined;
                    this._userSession = undefined;
                    this._SiteEntityClass = undefined;
                    this._sitePaths = undefined;
                    this._myUserInfo = undefined;
                    this._logger = undefined;
                    this._vendorMap = undefined;
                    this._typeBaseMap = undefined;
                    this._getUserInfoProm = undefined;
                    this.getActionGroupsProm = undefined;
                    this._slickFormatData = undefined;
                    this._modalUserFeedback = undefined;
                    this._activeOrgId = undefined;
                    this._customFieldsWithInherited = undefined;
                },
                (err) => {
                    this._logger.warn(
                        {error: err},
                        "ManageOrgsComponent.shutdown() Error shutting down ManageOrgsComponent"
                    );
                }
            );
    }

    async whenModelIsReady() {
        await this.fetchUserInfo();
        await this._loadAllSites();
        this._vendorMap = await this._getVendorMapDynamic();
        this._typeBaseMap = await this._getTypeBaseMapDynamic();
        this._descendantOrgs = await this._getDescendantOrgs();
        return await this._getActionGroupsFeed();
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _getUserInfo() {
        return this._userService.logMeInBro({});
    }

    _getDescendantOrgs() {
        return this._userService
            .getDescendantOrgs({
                id: this._userSession.activeOrganization.id,
            })
            .then(
                (descOrgs) => {
                    let descendantOrgs = {};
                    descendantOrgs = descOrgs;
                    descendantOrgs[this._activeOrgId] = {name: this._activeOrgName};
                    return descendantOrgs;
                },
                (err) => {
                    console.log("Unable to get your list of descendant orgs info.", err);
                }
            );
    }

    fetchUserInfo() {
        return this._getUserInfo()
            .then((result) => {
                this._myUserInfo = result;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.fetchUserInfo() error getting user info for org screen"
                );
            });
    }

    organizationChanged(orgObj) {
        if (this.activeOrgId === orgObj.id) {
            return;
        }
        this._activeOrgId = orgObj.id;
        this._activeOrgName = orgObj.name;
        this.emit("OrganizationChanged", orgObj);
    }

    feedUpdated() {
        this.emit("feedUpdated");
    }

    _getActionGroupsFeed() {
        return Promise.resolve()
            .then(() => {
                this._startActionGroupChangeFeed();

                return this.actionGroupsFeed;
            })
            .then((feed) => {
                return feed.whenInitialized();
            })
            .then((feed) => {
                this._registerFeedListeners();
                return this._convertRoleDataToSlickTableFormat(_.get(feed, "cache", {}));
            });
    }

    _startActionGroupChangeFeed() {
        if (_.isEmpty(this.actionGroupsFeed)) {
            this.actionGroupsFeed = this._actionGroupEntityDef.startAscendActionGroupsFeed(
                this.activeOrgId,
                {},
                {},
                {},
                {skip: 0, limit: 40 * 2}
            );
        } else {
            this.actionGroupsFeed.reestablishFeed(this.activeOrgId);
        }
    }

    _retrieveActionGroup(actionGroupId) {
        return Promise.resolve().then(() => {
            if (_.isEmpty(actionGroupId) || actionGroupId === 0) {
                return [];
            } else {
                return this._actionGroupEntityDef.loadAscendActionGroup(
                    this.activeOrgId,
                    actionGroupId,
                    {}
                );
            }
        });
    }

    _convertRoleDataToSlickTableFormat(roleData) {
        const canEdit = (this._canEdit =
            _.indexOf(_.get(this._myUserInfo, "authorizedActions", []), "actionGroupWrite") === -1
                ? false
                : true);
        roleData = Object.values(roleData).filter((role) => !role.getSystemInternal());
        const roleDataForSlickGrid = Object.keys(roleData).map((roleId) => {
            return Object.assign(
                {
                    id: _.get(roleData[roleId], "id"),
                    hasEditRights: canEdit,
                    isInherited:
                        _.get(roleData[roleId], "tenantId", this.activeOrgId) !== this.activeOrgId,
                },
                _.get(roleData[roleId], "pristineProperties", {})
            );
        });

        this._slickFormatData = roleDataForSlickGrid;
        return Promise.resolve();
    }

    getRoles() {
        return this._actionGroupEntityDef
            .loadAscendActionGroups(this.activeOrgId)
            .then((roles) => {
                roles = roles.filter((role) => !role.systemInternal);
                const rolesArray = roles.map((role) =>
                    Object.assign({
                        label: _.get(role, "pristineProperties.name"),
                        value: _.get(role, "id"),
                    })
                );
                rolesArray.unshift({label: "None", value: "none"});
                return rolesArray;
            })
            .catch((err) => {
                console.log("getRoles", err);
            });
    }

    setCustomFieldsEntity(newOrgInstance) {
        this._customFieldsEntityInstance = newOrgInstance;
        return this._customFieldsEntityInstance;
    }

    retrieveCustomFieldsEntity(tenantId) {
        if (this._customFieldsEntityInstance && this._customFieldsEntityInstance.shutdown) {
            this._customFieldsEntityInstance
                .shutdown()
                .catch((error) => this._logger.warn({error}));
        }
        return this._CustomFieldsEntityClass
            .retrieveCustomFields(tenantId)
            .then((customFieldsEntity) => {
                this.setCustomFieldsEntity(customFieldsEntity);
                return customFieldsEntity.loadInheritedFields();
            })
            .then((inheritedFields) => {
                Object.assign(
                    this._customFieldsWithInherited || {},
                    this._customFieldsEntityInstance.fields || {},
                    inheritedFields
                );
                return this._customFieldsWithInherited;
            });
    }

    deleteActionGroup(actionGroupId) {
        return this._retrieveActionGroup(actionGroupId).then((actionGroupEntity) => {
            return actionGroupEntity.delete();
        });
    }

    _resetPromises() {
        this._getUserInfoProm = this.fetchUserInfo();
        this.getActionGroupsProm = this._getActionGroupsFeed();
    }

    _getVendorMapDynamic() {
        return this._DeviceEntityClass.loadAndMergeVendorMap(this._activeOrgId);
    }

    _getTypeBaseMapDynamic() {
        return this._DeviceEntityClass.loadAndMergeTypeBaseMap(this._activeOrgId);
    }

    async _loadAllSites() {
        const sitePaths = await this._SiteEntityClass.loadSites(
            this._activeOrgId,
            {},
            {
                id: true,
                path: true,
                name: true,
                locationType: true,
            },
            {
                path: 1,
            }
        );
        this._sitePaths = sitePaths;
        return Promise.resolve();
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = ManageRolesComponent;
ManageRolesComponent.$groupName = "Organization";
ManageRolesComponent.$displayName = "Roles";
ManageRolesComponent.$inject = [
    "$teve.services.instance.User",
    "$teve.userSession",
    "$teve.logger",
    "$teve.entities.ActionGroup",
    "$teve.ui.userFeedback",
    "$teve.entities.CustomFields",
    "$teve.entities.Device",
    "$teve.entities.Site",
    "$teve.ui.UiUtils",
];
